import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import AiCreditsIndicator, {
  AiCredits,
} from 'components/AiAssistant/AiCreditsIndicator';
import { AiChatDocRef } from './AiChatInsertContext';
import ComposerDocChip from './ComposerDocChip';
import styles from './AiAssistant.module.scss';

// The composer no longer renders a thinking-mode toggle — see README.md
// ("Thinking-mode switcher") for why and how to bring one back.
type Props = {
  disabled?: boolean;
  // Whether an assistant reply is currently being generated — swaps the send
  // button for a stop button.
  isGenerating?: boolean;
  // Remaining AI credits for the composer footer; hidden when absent (no cap
  // configured, or AI budget not loaded). Provided by ConversationPane.
  credits?: AiCredits;
  placeholder: string;
  onSend: (content: string) => void;
  onStop?: () => void;
  // Documents pinned when the composer mounts (the document view's "Ask").
  initialDocs?: AiChatDocRef[];
};

// Imperative handle exposed to ConversationPane so an inline doc chip can pin a
// document reference to the composer (see AiChatInsertContext). The chip shows
// above the textarea; the `:documentation{id=…}` reference is folded into the
// message on send.
export type ChatInputHandle = {
  attachDocument: (doc: AiChatDocRef) => void;
};

// Grow the textarea with its content. The cap lives in CSS: once the field hits
// its max-height the textarea shrinks back into it and scrolls instead.
function autoResize(el: HTMLTextAreaElement | null) {
  if (!el) return;
  const node = el;
  node.style.height = 'auto';
  node.style.height = `${node.scrollHeight}px`;
}

function ChatInput(
  {
    disabled,
    isGenerating,
    credits,
    placeholder,
    onSend,
    onStop,
    initialDocs,
  }: Props,
  ref: React.Ref<ChatInputHandle>,
) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  // Documents pinned above the textarea; their `:documentation{id=…}` refs are
  // prepended to the message on send.
  const [attachedDocs, setAttachedDocs] = useState<AiChatDocRef[]>(
    () => initialDocs ?? [],
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Pin a document chip above the textarea (deduped by id) and focus the input.
  // Driven by the inline doc chip's "Insert into chat" action via
  // ConversationPane.
  useImperativeHandle(ref, () => ({
    attachDocument: (doc: AiChatDocRef) => {
      setAttachedDocs((prev) =>
        prev.some((existing) => existing.id === doc.id) ? prev : [...prev, doc],
      );
      textareaRef.current?.focus();
    },
  }));

  const removeDoc = (id: string) => {
    setAttachedDocs((prev) => prev.filter((doc) => doc.id !== id));
  };

  const submit = () => {
    const typed = value.trim();
    // Fold the pinned document chips into the message as directives so the BE
    // resolves them; prepended so they read as context for the typed prompt.
    const refs = attachedDocs.map((doc) => `:documentation{id=${doc.id}}`);
    const content = [...refs, typed].filter(Boolean).join(' ');
    if (!content || disabled) return;
    onSend(content);
    setValue('');
    setAttachedDocs([]);
    // Clear the inline height set by autoResize so the field snaps back to its
    // CSS min-height (measuring scrollHeight here would read the stale tall
    // content, since React hasn't re-rendered the now-empty textarea yet).
    if (textareaRef.current) textareaRef.current.style.height = '';
    textareaRef.current?.focus();
  };

  const showStop = !!isGenerating && !!onStop;
  const showSend = !showStop && (!!value.trim() || attachedDocs.length > 0);
  // The toolbar is empty when neither button applies — skip it so the field
  // doesn't carry the row's gap around nothing.
  const showToolbar = showStop || showSend;
  // Keep the textarea focusable while a reply is generating (so focus survives
  // submit and the user can type ahead); sending is still blocked by the submit
  // guard and the hidden send button. Only hard-disable for the cold-open case.
  const inputDisabled = !!disabled && !isGenerating;

  return (
    <div className={styles.inputArea}>
      <div className={styles.inputField}>
        {attachedDocs.length > 0 && (
          <div className={styles.composerDocs}>
            {attachedDocs.map((doc) => (
              <ComposerDocChip
                key={doc.id}
                name={doc.name}
                onRemove={() => removeDoc(doc.id)}
              />
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className={styles.inputTextarea}
          aria-label={t('ai-message-input-label')}
          placeholder={placeholder}
          rows={1}
          value={value}
          disabled={inputDisabled}
          onChange={(event) => {
            setValue(event.target.value);
            autoResize(event.target);
          }}
          onKeyDown={(event) => {
            // Skip Enter while an IME composition is in progress (CJK input
            // etc.) — that Enter confirms the composed characters, it isn't
            // the user asking to send.
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submit();
            }
          }}
        />
        {showToolbar && (
          <div className={styles.inputRow}>
            <div className={styles.inputActions}>
              {showStop && (
                <IconButton
                  icon="stop_circle"
                  type={IconButtonTypes.PrimaryOutlined}
                  size={IconButtonSizes.Small}
                  aria-label={t('ai-stop-generation')}
                  onClick={onStop}
                />
              )}
              {showSend && (
                <IconButton
                  icon="send"
                  type={IconButtonTypes.PrimaryFilled}
                  size={IconButtonSizes.Small}
                  aria-label={t('ai-send-message')}
                  disabled={disabled}
                  onClick={submit}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <AiCreditsIndicator credits={credits} />
    </div>
  );
}

export default forwardRef(ChatInput);
